import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Building2, Plus, Trash2, Loader2, RefreshCw, Users } from 'lucide-react';

const AZUL = '#0369A1';
const ROJO = '#DC2626';
const GRIS = '#6B7280';

export default function AdminCorporativos() {
  const [corps, setCorps] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState(null);
  const [abierto, setAbierto] = useState(null);
  const [sel, setSel] = useState('');

  const cargar = async () => {
    setCargando(true);
    try {
      const [a, b] = await Promise.all([
        api.get('/admin/corporativos'),
        api.get('/admin/clients').catch(() => ({ data: { clients: [] } })),
      ]);
      setCorps(a.data?.corporativos || []);
      const lista = b.data?.clients || b.data?.items || b.data || [];
      setClientes(Array.isArray(lista) ? lista : []);
    } catch (e) {
      toast.error('No se pudo cargar');
    } finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, []);

  const marcar = async (cli, valor) => {
    setOcupado(cli.id);
    try {
      await api.post(`/admin/clientes/${cli.id}/marcar-corporativo`,
        { es_corporativo: valor, nombre_grupo: cli.company_name || '' });
      toast.success(valor ? 'Marcado como corporativo' : 'Ya no es corporativo');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo');
    } finally { setOcupado(null); }
  };

  const asociar = async (corpId) => {
    if (!sel) { toast.error('Elige una unidad'); return; }
    setOcupado(corpId);
    try {
      await api.post(`/admin/corporativos/${corpId}/sucursales`, { client_id: sel, alias: '' });
      toast.success('Unidad asociada');
      setSel(''); cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo asociar');
    } finally { setOcupado(null); }
  };

  const quitar = async (corpId, sucId, nombre) => {
    if (!window.confirm(`Quitar "${nombre}" del grupo?\n\nLa unidad conserva su cuenta y su historial.`)) return;
    setOcupado(corpId);
    try {
      await api.delete(`/admin/corporativos/${corpId}/sucursales/${sucId}`);
      toast.success('Unidad desasociada'); cargar();
    } catch (e) {
      toast.error('No se pudo quitar');
    } finally { setOcupado(null); }
  };

  const idsCorp = new Set(corps.map((c) => c.id));
  const disponibles = clientes.filter((c) => !idsCorp.has(c.id));
  const noCorporativos = clientes.filter((c) => !idsCorp.has(c.id) && !c.corporativo_id);

  return (
    <AdminLayout title="Corporativos">
      <div style={{ maxWidth: 1050 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Corporativos</h1>
          <button onClick={cargar} disabled={cargando}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              border: '1px solid #DDD', borderRadius: 8, background: '#FFF', cursor: 'pointer' }}>
            <RefreshCw size={15} /> Actualizar
          </button>
        </div>
        <p style={{ color: GRIS, fontSize: 14, marginTop: 6, marginBottom: 24 }}>
          Un corporativo es un cliente que agrupa varias unidades (plazas, sucursales,
          restaurantes). Al entrar a su cuenta ve el estado de todas sus unidades en un
          solo panel; no necesita ninguna direccion especial.
        </p>

        {cargando ? (
          <div style={{ display: 'flex', gap: 10, color: GRIS, padding: 40 }}>
            <Loader2 size={18} className="animate-spin" /> Cargando...
          </div>
        ) : (
          <>
            {corps.length > 0 && (
              <div style={{ display: 'grid', gap: 14, marginBottom: 30 }}>
                {corps.map((c) => (
                  <div key={c.id} style={{ border: '1px solid #E5E7EB', borderRadius: 12,
                    padding: 18, background: '#FFF' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Building2 size={17} color={AZUL} />
                          <span style={{ fontWeight: 700, fontSize: 16 }}>{c.nombre}</span>
                        </div>
                        <div style={{ color: GRIS, fontSize: 12.5, marginTop: 3 }}>
                          {c.email} · {c.num_sucursales} unidad(es)
                        </div>
                      </div>
                      <button onClick={() => marcar({ id: c.id, company_name: c.nombre }, false)}
                        disabled={ocupado === c.id}
                        style={{ padding: '6px 11px', border: '1px solid #DDD', borderRadius: 7,
                          background: '#FFF', color: GRIS, cursor: 'pointer', fontSize: 12.5 }}>
                        Quitar corporativo
                      </button>
                    </div>

                    <div style={{ marginTop: 14, paddingTop: 13, borderTop: '1px solid #F0F0F0' }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: GRIS, marginBottom: 9 }}>
                        UNIDADES DEL GRUPO
                      </div>
                      {!c.sucursales?.length ? (
                        <div style={{ fontSize: 13, color: GRIS, marginBottom: 11 }}>
                          Sin unidades asociadas todavia.
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
                          {c.sucursales.map((s) => (
                            <div key={s.client_id} style={{ display: 'flex',
                              justifyContent: 'space-between', alignItems: 'center',
                              padding: '8px 12px', background: '#F8F9FA', borderRadius: 7,
                              fontSize: 13.5 }}>
                              <span>{s.alias || s.nombre}</span>
                              <button onClick={() => quitar(c.id, s.client_id, s.alias || s.nombre)}
                                title="Quitar del grupo"
                                style={{ border: 'none', background: 'transparent',
                                  color: ROJO, cursor: 'pointer', padding: 3 }}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <select value={abierto === c.id ? sel : ''}
                          onChange={(e) => { setAbierto(c.id); setSel(e.target.value); }}
                          style={{ flex: 1, padding: '8px 11px', border: '1px solid #DDD',
                            borderRadius: 7, fontSize: 13 }}>
                          <option value="">Elige una unidad para asociar...</option>
                          {disponibles.filter((d) => d.id !== c.id).map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.company_name || d.name || d.email}
                            </option>
                          ))}
                        </select>
                        <button onClick={() => asociar(c.id)} disabled={ocupado === c.id}
                          style={{ display: 'flex', alignItems: 'center', gap: 5,
                            padding: '8px 14px', border: 'none', borderRadius: 7,
                            background: AZUL, color: '#FFF', cursor: 'pointer', fontSize: 13 }}>
                          {ocupado === c.id ? <Loader2 size={14} className="animate-spin" />
                            : <Plus size={14} />} Asociar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 5px' }}>
              Convertir un cliente en corporativo
            </h2>
            <p style={{ color: GRIS, fontSize: 13, marginTop: 0, marginBottom: 13 }}>
              Marca aqui al cliente que agrupa varias unidades.
            </p>
            {!noCorporativos.length ? (
              <div style={{ padding: 26, textAlign: 'center', color: GRIS,
                border: '1px dashed #DDD', borderRadius: 10, fontSize: 13.5 }}>
                <Users size={30} style={{ opacity: 0.35, marginBottom: 8 }} />
                <div>No hay clientes disponibles para convertir.</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 7 }}>
                {noCorporativos.map((cl) => (
                  <div key={cl.id} style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', padding: '11px 15px', border: '1px solid #E5E7EB',
                    borderRadius: 9, fontSize: 13.5 }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{cl.company_name || cl.name || '(sin nombre)'}</div>
                      <div style={{ color: GRIS, fontSize: 12 }}>{cl.email}</div>
                    </div>
                    <button onClick={() => marcar(cl, true)} disabled={ocupado === cl.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 13px',
                        border: '1px solid ' + AZUL, borderRadius: 7, background: '#FFF',
                        color: AZUL, cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>
                      {ocupado === cl.id ? <Loader2 size={13} className="animate-spin" />
                        : <Building2 size={13} />} Marcar corporativo
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
